import { useEffect, useMemo, useRef } from 'react';
import { Location, useLocation, useSearchParams } from 'react-router-dom';
import { PageDto } from '@asset-metrix/portal-gateway-client-js';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  fetchNavigationCustomization,
  updateNavigationCustomization,
} from '../api/portalGateway/navigationCustomization';
import {
  fetchNavItemLabelLines,
  NavItemLabelLinesQueryKey,
} from '../api/portalGateway/navItemLabelLines';
import { useStore } from '../store';
import { useCombinedPageFilterSelections } from '../store/hooks/useCombinedPageFilterSelections';
import { FilterConfigDto } from '../store/states/filterTypes';
import { COMPARISON_ENTITIES_URL_PARAM } from '../utils/constants';
import { DetailPageNavigationState } from '../utils/detailPageLinkDisplay';
import { getDetailPageNavigationIdentity } from '../utils/detailPageNavigationIdentity';
import {
  buildGenerateNavItemLabelLinesRequest,
  fallbackNavItemLabelsWhenEmpty,
  hashHiddenNavItemLabelSelection,
  mapNavItemLabelLinesToDisplay,
  navItemLabelsEqual,
} from '../utils/detailPageNavItemLabelLines';
import { prependRoleId } from '../utils/prependRoleId';

import { useReactiveCachedPageFilterDefinitions } from './useReactiveCachedPageFilterDefinitions';

type UseGenerateDetailPageNavItemLabelsParams = {
  pageId: string | null;
  pageData?: PageDto;
  instanceId: string | null;
  navItemId: string | null;
  pageType: 'page' | 'detailPage';
};

export function useGenerateDetailPageNavItemLabels({
  pageId,
  pageData,
  instanceId,
  navItemId,
  pageType,
}: UseGenerateDetailPageNavItemLabelsParams) {
  const [searchParams] = useSearchParams();
  const { state }: Location<DetailPageNavigationState | null> = useLocation();
  const queryClient = useQueryClient();
  const updateMutation = useUpdateNavigationCustomizationMutation();

  const combinedSelections = useCombinedPageFilterSelections(instanceId ?? '');
  const cachedPageFilterDefinitions =
    useReactiveCachedPageFilterDefinitions(instanceId);

  const [filterInitialized, filterQueryLoaded] = useStore(
    useShallow((store) => [
      instanceId != null
        ? (store.filterInitialized[instanceId] ?? false)
        : false,
      instanceId != null
        ? (store.filterQueryLoaded[instanceId] ?? false)
        : false,
    ])
  );

  const identity = useMemo(
    () => getDetailPageNavigationIdentity(searchParams, pageData),
    [searchParams, pageData]
  );

  const requestBody = useMemo(() => {
    if (identity == null) {
      return null;
    }
    return buildGenerateNavItemLabelLinesRequest(identity, combinedSelections);
  }, [identity, combinedSelections]);

  const hiddenSelectionHash = useMemo(() => {
    if (identity == null) {
      return '';
    }
    return hashHiddenNavItemLabelSelection(identity, combinedSelections);
  }, [identity, combinedSelections]);

  const navigationCustomizationData = queryClient.getQueryData(
    getNavigationCustomizationQueryOptions().queryKey
  );

  const navItemExists =
    navItemId != null &&
    navigationCustomizationData?.data.dynamicNavigationItems.some(
      (item) => item.id === navItemId
    );

  const hasConfiguredNavItemLabels =
    (pageData?.detailPageNavItemConfig?.length ?? 0) > 0;
  const isComparisonPage = searchParams.has(COMPARISON_ENTITIES_URL_PARAM);

  const queryEnabled =
    pageType === 'detailPage' &&
    hasConfiguredNavItemLabels &&
    !isComparisonPage &&
    pageId != null &&
    navItemId != null &&
    navItemExists &&
    instanceId != null &&
    filterInitialized &&
    filterQueryLoaded &&
    identity != null &&
    requestBody != null &&
    Object.keys(requestBody.selection).length > 0;

  const labelLinesQuery = useQuery({
    queryKey: prependRoleId<NavItemLabelLinesQueryKey>([
      'navItemLabelLines',
      pageId ?? 'unknown',
      navItemId ?? 'unknown',
      identity?.signature ?? '',
      hiddenSelectionHash,
      requestBody ?? { detailsDiscriminatorKey: '', selection: {} },
    ]),
    queryFn: fetchNavItemLabelLines,
    enabled: queryEnabled,
  });

  const updateMutationRef = useRef(updateMutation);
  updateMutationRef.current = updateMutation;

  const locationStateRef = useRef(state);
  locationStateRef.current = state;

  useEffect(() => {
    if (
      !labelLinesQuery.isSuccess ||
      labelLinesQuery.data == null ||
      navItemId == null ||
      pageData == null ||
      identity == null
    ) {
      return;
    }

    const filterConfigByKey = Object.fromEntries(
      (pageData.pageFilter?.filters ?? []).map((filter: FilterConfigDto) => [
        filter.key,
        filter,
      ])
    );

    const mappedLabels = mapNavItemLabelLinesToDisplay(
      labelLinesQuery.data.data.lines,
      {
        pageData,
        detailsDiscriminatorKey: identity.detailsDiscriminator,
        filterConfigByKey,
        detailsDiscriminatorDisplayValue:
          locationStateRef.current?.detailsDiscriminatorDisplayValue,
        pageFilterDefinitions: cachedPageFilterDefinitions,
      }
    );

    const nextLabels =
      labelLinesQuery.data.data.lines.length === 0
        ? fallbackNavItemLabelsWhenEmpty(pageData)
        : mappedLabels;

    const previousNavigationCustomization = queryClient.getQueryData(
      getNavigationCustomizationQueryOptions().queryKey
    );
    const previousItem =
      previousNavigationCustomization?.data.dynamicNavigationItems.find(
        (item) => item.id === navItemId
      );

    if (
      previousItem == null ||
      navItemLabelsEqual(previousItem.navItemLabels ?? [], nextLabels) ||
      updateMutationRef.current.isPending
    ) {
      return;
    }

    updateMutationRef.current.mutate({
      data: {
        id: navItemId,
        href: previousItem.href,
        icon: previousItem.icon,
        navItemLabels: nextLabels,
      },
    });
  }, [
    cachedPageFilterDefinitions,
    identity,
    labelLinesQuery.data,
    labelLinesQuery.isSuccess,
    navItemId,
    pageData,
    queryClient,
  ]);
}

function getNavigationCustomizationQueryOptions() {
  return queryOptions({
    queryKey: prependRoleId(['navigationCustomization']),
    queryFn: fetchNavigationCustomization,
  });
}

function useUpdateNavigationCustomizationMutation() {
  const queryClient = useQueryClient();
  const options = getNavigationCustomizationQueryOptions();

  return useMutation({
    mutationFn: updateNavigationCustomization,
    onMutate: async (newNavItem) => {
      await queryClient.cancelQueries(options);
      const previousNavigationCustomization = queryClient.getQueryData(
        options.queryKey
      );

      queryClient.setQueryData(options.queryKey, (old) =>
        old
          ? {
              ...old,
              data: {
                dynamicNavigationItems: old.data.dynamicNavigationItems.map(
                  (navItem) =>
                    navItem.id === newNavItem.data.id
                      ? { ...navItem, ...newNavItem.data }
                      : navItem
                ),
              },
            }
          : undefined
      );

      return { previousNavigationCustomization };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(
        options.queryKey,
        context?.previousNavigationCustomization
      );
    },
  });
}
